import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTimeout } from '@mantine/hooks';
import clsx from 'clsx';

import { GoogleFontIcon } from '../icons/GoogleFontIcon';

import { CitationText } from './CitationText';
import { composerSafeButtonHandlers } from './composerSafeButtonHandlers';
import { COPY_FEEDBACK_MS } from './constants';
import { DocumentContextChips, PageContextChips } from './PageContextChips';
import { resolveMessageSourceGroups } from './replyPageSources';
import type { AiAssistantMessage as AiAssistantMessageModel } from './types';

import styles from './AiAssistantMessage.module.scss';

type AiAssistantMessageProps = {
  isSourcesActive: boolean;
  message: AiAssistantMessageModel;
  onCopy: (message: AiAssistantMessageModel) => Promise<boolean>;
  onJumpToSource: (message: AiAssistantMessageModel, number: number) => void;
  onNavigate?: (to: string) => void;
  onOpenSources: (message: AiAssistantMessageModel) => void;
};

type StreamingStatusListProps = {
  className?: string;
  completedStatuses: string[];
  currentStatuses: string[];
  messageId: string;
};

function StreamingStatusList({
  className,
  completedStatuses,
  currentStatuses,
  messageId,
}: StreamingStatusListProps) {
  const { t } = useTranslation();
  const liveStatuses =
    currentStatuses.length > 0
      ? currentStatuses
      : [t('app.aiAssistant.thinking')];

  return (
    <div
      className={clsx(styles['status-list'], className)}
      data-testid={`ai-assistant-status-list-${messageId}`}
    >
      {completedStatuses.map((label, index) => (
        <div
          className={styles['status-done']}
          data-testid={`ai-assistant-status-done-${messageId}`}
          key={`done:${index}:${label}`}
        >
          {label}
        </div>
      ))}
      {liveStatuses.map((label, index) => (
        <div
          className={styles['status-current']}
          data-testid={`ai-assistant-status-${messageId}`}
          key={`live:${index}:${label}`}
        >
          <span aria-hidden className={styles['status-dots']}>
            <span className={styles['dot']} />
            <span className={styles['dot']} />
            <span className={styles['dot']} />
            <span className={styles['dot']} />
            <span className={styles['dot']} />
            <span className={styles['dot']} />
          </span>
          <span className={styles['generating-label']}>{label}</span>
        </div>
      ))}
    </div>
  );
}

export function AiAssistantMessage({
  isSourcesActive,
  message,
  onCopy,
  onJumpToSource,
  onNavigate,
  onOpenSources,
}: AiAssistantMessageProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const { clear, start } = useTimeout(() => setCopied(false), COPY_FEEDBACK_MS);
  const { sources, text } = message;
  const sourceGroups = useMemo(
    () => resolveMessageSourceGroups({ sources, text }),
    [sources, text]
  );
  const isReceivedMessage = message.sender === 'assistant';
  const hasSources = sourceGroups.length > 0;
  const sourcesToggleCount = sourceGroups.length;
  const isStreamingReply = isReceivedMessage && message.complete === false;
  const hasReplyText = message.text.trim() !== '';
  const currentStatuses = (message.statuses ?? []).map(
    (status) => status.content
  );
  const completedStatuses = (message.completedStatuses ?? []).map(
    (status) => status.content
  );

  async function handleCopy() {
    if (await onCopy(message)) {
      clear();
      setCopied(true);
      start();
    }
  }

  const statusList = isStreamingReply ? (
    <StreamingStatusList
      className={clsx(styles['generating'], styles['message'])}
      completedStatuses={completedStatuses}
      currentStatuses={currentStatuses}
      messageId={message.id}
    />
  ) : null;

  if (isStreamingReply && !hasReplyText) {
    return statusList;
  }

  return (
    <>
      {statusList}
      <div
        className={clsx(styles['message'], {
          [styles['message--assistant']]: isReceivedMessage,
          [styles['message--user']]: !isReceivedMessage,
          [styles['message--sources-active']]:
            isSourcesActive && !isStreamingReply,
        })}
        data-testid={`ai-assistant-message-${message.id}`}
      >
        <CitationText
          message={message}
          onJumpToSource={onJumpToSource}
          onNavigate={onNavigate}
        />
        {!isReceivedMessage && message.pageContexts?.length ? (
          <PageContextChips
            className={styles['page-contexts']}
            items={message.pageContexts}
            onNavigate={onNavigate}
            tone="onPrimary"
          />
        ) : null}
        {!isReceivedMessage && message.documentContexts?.length ? (
          <DocumentContextChips
            className={styles['page-contexts']}
            items={message.documentContexts}
            tone="onPrimary"
          />
        ) : null}
        {isReceivedMessage && !isStreamingReply ? (
          <div className={styles['message-actions']}>
            <button
              className={styles['message-action']}
              data-testid={`ai-assistant-copy-${message.id}`}
              {...composerSafeButtonHandlers(() => {
                void handleCopy();
              })}
              title={
                copied
                  ? t('app.aiAssistant.copied')
                  : t('app.aiAssistant.copyReply')
              }
              type="button"
            >
              <span aria-hidden className={styles['copy-icon']}>
                <GoogleFontIcon
                  className={clsx(
                    styles['copy-icon__glyph'],
                    copied && styles['copy-icon__glyph--hidden']
                  )}
                  name="content_copy"
                  solid
                />
                <GoogleFontIcon
                  className={clsx(
                    styles['copy-icon__glyph'],
                    !copied && styles['copy-icon__glyph--hidden']
                  )}
                  name="check"
                  solid
                />
              </span>
            </button>
            {hasSources ? (
              <button
                aria-expanded={isSourcesActive}
                className={styles['message-action']}
                data-testid={`ai-assistant-sources-toggle-${message.id}`}
                {...composerSafeButtonHandlers(() => onOpenSources(message))}
                title={
                  isSourcesActive
                    ? t('app.aiAssistant.hideSources', {
                        count: sourcesToggleCount,
                      })
                    : t('app.aiAssistant.showSources', {
                        count: sourcesToggleCount,
                      })
                }
                type="button"
              >
                <GoogleFontIcon
                  name={isSourcesActive ? 'menu_book' : 'book_2'}
                  solid
                />
                <span className={styles['message-sources__count']}>
                  ({sourcesToggleCount})
                </span>
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </>
  );
}
